import { useCallback, useEffect, useRef, useState } from 'react'
import { desktopNotificationsApi } from '~/apis/desktopNotificationsApi'
import { useAuthenticationState } from '~/atoms/AuthenticationState'
import { useUserState } from '~/atoms/UserState'
import { PartnerId, useInstalledAppPartner } from '~/hooks/useCurrentPartner'

export interface ReleaseNotificationsHookResult {
  /** Null until the current subscription state has loaded. */
  isSubscribed: boolean | null
  /**
   * True when the loaded row exists with status 'unsubscribed' — i.e. the user
   * opted out before, as opposed to never having subscribed. Lets the UI warn
   * that re-enabling will re-subscribe them (the backend clears their Mailgun
   * suppression on opt-in). False while loading or with no prior row.
   */
  wasUnsubscribed: boolean
  isLoading: boolean
  error: string | null
  /**
   * Re-runs the subscription load. Wired to the retry button the error alert
   * shows on a failed load, so users can recover in place instead of leaving
   * Settings and coming back, which was previously the only way to re-trigger it.
   */
  refetch: () => Promise<void>
  /**
   * True only when the subscription is actually loadable/toggleable: the user is
   * logged in, the channel resolved to a usable (truthy) value, and we have the
   * account email `setSubscribed` needs. Mirrors `loadState`'s own guard plus the
   * email requirement, so a non-loadable state (channel resolution failed, an
   * empty-string channel, logged-out, or no account email) falls through to the
   * disabled toggle instead of a clickable-but-erroring ('not_ready') control or
   * an endless spinner.
   */
  isReady: boolean
  /** The account email the notifications are sent to (undefined until loaded). */
  recipientEmail: string | undefined
  /** Subscribe/unsubscribe the current user for their current channel. */
  setSubscribed: (subscribed: boolean) => Promise<void>
}

/**
 * Reads + toggles the current user's desktop release-notification subscription
 * for their current channel, via customer-service behind the API gateway. The
 * in-app toggle IS the opt-in (authenticated + intentional), so no double
 * opt-in round-trip — matches the service's v1 contract (CUS-590).
 */
// The release fan-out keys subscriptions by the bucket-derived slug the
// normalizer emits (the publish bucket minus `electron-`), NOT the electron
// config partnerId of the installed build. For the base build
// those differ (`''` -> bucket electron-mango-voice -> `mango-voice`), and for
// the partners the bucket carries a `-phones` suffix the config id lacks:
//   ''            -> mango-voice           (bucket electron-mango-voice)
//   dentrix       -> dentrix-phones        (bucket electron-dentrix-phones)
//   dentrix-ascend-> dentrix-ascend-phones (bucket electron-dentrix-ascend-phones)
// A subscription must be stored under the slug or the Expander (which matches
// stored partner_id == event partnerId, no normalization) finds zero subscribers
// and sends nothing. Only the default case was normalized before, so dentrix /
// dentrix-ascend users were subscribed under a key no release ever matched
// (CUS-590).
// Keyed by PartnerId so a typo or a stale key is a compile error, and adding a
// partner to the union surfaces here. Partial: only ids whose slug differs from
// the config id need an entry.
const RELEASE_PARTNER_ID_BY_CONFIG_ID: Partial<Record<PartnerId, string>> = {
  '': 'mango-voice',
  dentrix: 'dentrix-phones',
  'dentrix-ascend': 'dentrix-ascend-phones',
}

// Fall back to the raw partner key for any partner without an explicit mapping.
// A partner whose config id already equals its bucket slug needs no entry; one
// that doesn't (a future `-phones`-style bucket) must be added above, same as its
// release bucket is added to the notifier's release_bucket_names.
const releasePartnerId = (configPartnerId: PartnerId): string =>
  RELEASE_PARTNER_ID_BY_CONFIG_ID[configPartnerId] ?? configPartnerId

// Auto-retry a flaky load a couple of times before surfacing the red error
// state. The first fetch after opening Settings occasionally blips, and until
// now only leaving and re-entering the screen re-triggered it. Linear backoff
// (400ms, 800ms) keeps the total wait short while the spinner is still showing.
const LOAD_MAX_ATTEMPTS = 3
const LOAD_RETRY_BASE_MS = 400
const delay = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms))

export const useReleaseNotifications = (): ReleaseNotificationsHookResult => {
  const { isUserLoggedIn } = useAuthenticationState()
  const { sessionUser } = useUserState()
  // The installed build's partner only. A PBX partner key must not leak in: an
  // Internal-build user on a Dentrix PBX would otherwise read and write the
  // dentrix-phones subscription. Null until resolved, which holds off load/toggle.
  const installedPartner = useInstalledAppPartner()
  const partnerId =
    installedPartner === null ? null : releasePartnerId(installedPartner)

  const [isSubscribed, setIsSubscribed] = useState<boolean | null>(null)
  const [wasUnsubscribed, setWasUnsubscribed] = useState<boolean>(false)
  const [channel, setChannel] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const email = sessionUser?.email

  // Guards state writes across the auto-retry backoff so a load that resolves
  // after the user has left Settings doesn't set state on an unmounted hook.
  // Re-armed on every mount (StrictMode remount keeps the ref instance).
  const isMountedRef = useRef(true)
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Supersession guard for loadState: each run claims the next generation, and a
  // run that gets superseded mid-backoff (deps changed, or a manual refetch) sees
  // a newer generation and bails rather than writing state computed against a
  // stale channel/partnerId. isMountedRef only covers unmount, not this.
  const loadGenerationRef = useRef(0)

  // Resolve the current channel from Electron: prefer the running build's
  // channel (getBuildEnv), fall back to the saved preference (getCurrentChannel).
  useEffect(() => {
    let cancelled = false
    const resolveChannel = async () => {
      try {
        const buildEnv = await window.electron?.channels?.getBuildEnv()
        const current =
          buildEnv || (await window.electron?.channels?.getCurrentChannel())
        if (!cancelled) setChannel(current ?? null)
      } catch {
        if (!cancelled) setChannel(null)
      }
    }
    void resolveChannel()
    return () => {
      cancelled = true
    }
  }, [])

  const loadState = useCallback(async () => {
    if (!isUserLoggedIn || !channel || !partnerId) return
    const generation = ++loadGenerationRef.current
    // Still the current, mounted run? Any newer loadState bumps the generation,
    // so a superseded run stops here instead of clobbering fresh state.
    const isCurrent = () =>
      isMountedRef.current && loadGenerationRef.current === generation
    setIsLoading(true)
    setError(null)
    try {
      for (let attempt = 1; attempt <= LOAD_MAX_ATTEMPTS; attempt++) {
        try {
          const res = await desktopNotificationsApi.listSubscriptions()
          if (!isCurrent()) return
          if (res.ok) {
            const match = res.data?.subscriptions?.find(
              (s) => s.partner_id === partnerId && s.channel === channel,
            )
            setIsSubscribed(match?.status === 'confirmed')
            // Both 'unsubscribed' (in-app / one-click) and 'suppressed'
            // (Mailgun webhook: complaint, bounce, or a Mailgun-footer
            // unsubscribe) mean the user opted out and re-enabling re-subscribes
            // them, so both drive the re-subscribe note.
            setWasUnsubscribed(
              match?.status === 'unsubscribed' ||
                match?.status === 'suppressed',
            )
            return
          }
        } catch {
          // A thrown request counts as a failed attempt; fall through to retry.
        }
        if (attempt < LOAD_MAX_ATTEMPTS) {
          await delay(LOAD_RETRY_BASE_MS * attempt)
          if (!isCurrent()) return
        }
      }
      setError('load_failed')
    } finally {
      if (isCurrent()) setIsLoading(false)
    }
  }, [isUserLoggedIn, channel, partnerId])

  useEffect(() => {
    void loadState()
  }, [loadState])

  const setSubscribed = useCallback(
    async (subscribed: boolean) => {
      if (!channel || !partnerId || !email) {
        setError('not_ready')
        return
      }
      setIsLoading(true)
      setError(null)
      // Optimistic flip; revert on failure.
      const previous = isSubscribed
      const previousWasUnsubscribed = wasUnsubscribed
      setIsSubscribed(subscribed)
      // A successful opt-in clears the "previously unsubscribed" state so the
      // re-subscribe note disappears; opting back out sets it again.
      setWasUnsubscribed(!subscribed)
      try {
        const res = subscribed
          ? await desktopNotificationsApi.subscribe({
              partnerId,
              channel,
              endpoint: email,
              protocol: 'email',
            })
          : await desktopNotificationsApi.unsubscribe({
              partnerId,
              channel,
            })
        if (!res.ok) {
          setIsSubscribed(previous)
          setWasUnsubscribed(previousWasUnsubscribed)
          setError('update_failed')
        }
      } catch {
        setIsSubscribed(previous)
        setWasUnsubscribed(previousWasUnsubscribed)
        setError('update_failed')
      } finally {
        setIsLoading(false)
      }
    },
    [channel, partnerId, email, isSubscribed, wasUnsubscribed],
  )

  return {
    isSubscribed,
    wasUnsubscribed,
    isLoading,
    error,
    // Truthy channel (not just non-null) to match loadState's guard — an
    // empty-string channel isn't loadable — plus a resolved partner, logged-in,
    // and an email to send to, which setSubscribed requires. Keeps isReady in lockstep with what can
    // actually load/toggle, so the UI never spins or fires 'not_ready' clicks.
    isReady:
      isUserLoggedIn &&
      Boolean(channel) &&
      Boolean(partnerId) &&
      Boolean(email),
    recipientEmail: email,
    setSubscribed,
    refetch: loadState,
  }
}
